import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/organization/roles")({
  component: RouteComponent,
});

function RouteComponent() {
  // Role administration is what makes someone an admin, so the screen that
  // hands it out is gated on the same capability it hands out.
  return (
    <ProtectedRoute requiredPermission="users.role.manage">
      <RolesPage />
    </ProtectedRoute>
  );
}

import { useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Loader2 } from "lucide-react";

import { ProtectedRoute } from "@/components/protected-route";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { RoleFormSheet } from "@/components/authorization/RoleFormSheet";
import { RoleTable } from "@/components/crud/table/RoleTable";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SidebarInset } from "@/components/ui/sidebar";
import {
  useCreateRole,
  useDeleteRole,
  useDuplicateRole,
  useRoles,
  useUpdateRole,
} from "@/hooks/api/authorization/useAuthorization";
import { formatError } from "@/lib/error";
import type { Role, RolePayload } from "@/types/authorization";

function RolesPage() {
  const rolesQuery = useRoles();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();
  const duplicateRole = useDuplicateRole();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [roleToDuplicate, setRoleToDuplicate] = useState<Role | null>(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  const roles = rolesQuery.data ?? [];
  const isMutating =
    createRole.isPending || updateRole.isPending || duplicateRole.isPending;

  const handleCreate = () => {
    setEditingRole(null);
    setIsSheetOpen(true);
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setIsSheetOpen(true);
  };

  // Errors are re-thrown so the sheet can render the server's own words next
  // to the fields that caused them — the "somebody must be able to manage
  // roles" refusal is an explanation, not a failure code.
  const handleSubmit = async (payload: RolePayload) => {
    if (editingRole) {
      await updateRole.mutateAsync({ id: editingRole.id, ...payload });
      toast.success("Role updated.");
    } else {
      await createRole.mutateAsync(payload);
      toast.success("Role created.");
    }
    setIsSheetOpen(false);
  };

  const handleDeleteClick = (role: Role) => {
    setDeleteError(null);
    setRoleToDelete(role);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;
    try {
      await deleteRole.mutateAsync(roleToDelete.id);
      toast.success("Role deleted.");
      setRoleToDelete(null);
    } catch (error) {
      setDeleteError(formatError(error, "Couldn't delete the role."));
    }
  };

  const handleDuplicateClick = (role: Role) => {
    setDuplicateError(null);
    setDuplicateName(`${role.name} (copy)`);
    setRoleToDuplicate(role);
  };

  const handleDuplicateConfirm = async () => {
    if (!roleToDuplicate) return;
    try {
      await duplicateRole.mutateAsync({
        id: roleToDuplicate.id,
        name: duplicateName.trim(),
      });
      toast.success("Role duplicated.");
      setRoleToDuplicate(null);
    } catch (error) {
      setDuplicateError(formatError(error, "Couldn't duplicate the role."));
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Roles & Permissions"
          description="A role widens what its holders may see beyond their own records. Everyone keeps their own records with no role at all."
          onAdd={handleCreate}
          addLabel="Create Role"
          isLoading={rolesQuery.isFetching || isMutating}
        />

        {rolesQuery.isError && (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>
              {formatError(rolesQuery.error, "Couldn't load roles.")}
            </AlertDescription>
          </Alert>
        )}

        <RoleTable
          roles={roles}
          isLoading={rolesQuery.isPending}
          onEdit={handleEdit}
          onDuplicate={handleDuplicateClick}
          onDelete={handleDeleteClick}
        />

        <RoleFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          editingRole={editingRole}
          isLoading={createRole.isPending || updateRole.isPending}
        />

        <DeleteConfirmDialog
          isOpen={Boolean(roleToDelete)}
          onClose={() => setRoleToDelete(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Role"
          itemName={roleToDelete?.name}
          isLoading={deleteRole.isPending}
          destructive
          description={
            <div className="space-y-2">
              <p>
                Delete the role &quot;{roleToDelete?.name}&quot;?{" "}
                {roleToDelete?.user_count === 0 ? (
                  <span>Nobody currently holds it.</span>
                ) : (
                  <span className="font-medium text-foreground">
                    {roleToDelete?.user_count}{" "}
                    {roleToDelete?.user_count === 1
                      ? "person holds"
                      : "people hold"}{" "}
                    it.
                  </span>
                )}
              </p>
              <p>
                A role that someone holds cannot be deleted — reassign its
                holders first, or the deletion will be refused.
              </p>
              {deleteError && (
                <Alert variant="destructive">
                  <AlertCircle />
                  <AlertDescription>{deleteError}</AlertDescription>
                </Alert>
              )}
            </div>
          }
        />

        <Dialog
          open={Boolean(roleToDuplicate)}
          onOpenChange={(open) => !open && setRoleToDuplicate(null)}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Duplicate Role</DialogTitle>
              <DialogDescription>
                Copies every permission and scope from &quot;
                {roleToDuplicate?.name}&quot; into a new role. Nobody is
                assigned to it.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Label htmlFor="duplicate-name">New Role Name</Label>
              <Input
                id="duplicate-name"
                value={duplicateName}
                onChange={(event) => setDuplicateName(event.target.value)}
              />
            </div>
            {duplicateError && (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertDescription>{duplicateError}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRoleToDuplicate(null)}
                disabled={duplicateRole.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDuplicateConfirm}
                disabled={duplicateRole.isPending || !duplicateName.trim()}
              >
                {duplicateRole.isPending && (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                )}
                Duplicate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SidebarInset>
  );
}
